// @file state and actions for the Grading panel
import { trackEvent } from '@@/bits/analytics'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { clearSearchParam, setSearchParam } from '@@/bits/location'
import {
  Gradable,
  GradeCategory,
  LmsSyncState,
  SnackbarNotificationType,
  UserSyncState,
  WallGradeCalculationMethods,
} from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import PadletApi from '@@/surface/padlet_api'
import type {
  CalculatedWallGrade,
  Grade,
  Id,
  JsonApiData,
  LmsPassbackResponse,
  SyncStateUserIds,
  WallGradingSettings,
} from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
export const useSurfaceGradingPanelStore = defineStore('surfaceGradingPanel', () => {
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  // State
  const xSurfaceGradingPanel = ref(false)

  const isAwaitingGradingPanelApiResponse = ref<boolean>(true)
  const gradingSettings = ref<WallGradingSettings | null>(null)
  const grades = ref<Grade[]>([])
  const syncStateToUserId = ref<SyncStateUserIds | null>(null)
  const calculatedWallGrades = computed((): CalculatedWallGrade[] => {
    const userGradesMap = new Map<string, number[]>()

    // Populate the grades map
    grades.value.forEach((grade) => {
      if (grade.category !== GradeCategory.Score || grade.gradableType === Gradable.Wall) {
        return
      }

      const userId = grade.gradedUserId?.toString()
      const value = Number(grade.value)

      if (!userGradesMap.has(userId)) {
        userGradesMap.set(userId, [])
      }

      userGradesMap.get(userId)?.push(value)
    })

    // Clear the calculatedWallGrades array
    const calculatedWallGrades: CalculatedWallGrade[] = []

    // Populate calculatedWallGrades from the userGradesMap
    userGradesMap.forEach((userGrades, userId) => {
      const calculatedGrade = {
        authorId: Number(userId),
        grades: userGrades,
        calculatedGrade: 0,
      }

      const calculationMethod = gradingSettings.value?.gradeCalculationMethod
      if (calculationMethod === WallGradeCalculationMethods.Average) {
        calculatedGrade.calculatedGrade = userGrades.reduce((a, b) => a + b, 0) / userGrades.length
      } else if (calculationMethod === WallGradeCalculationMethods.Highest) {
        calculatedGrade.calculatedGrade = Math.max(...userGrades)
      }
      calculatedWallGrades.push(calculatedGrade)
    })

    return calculatedWallGrades
  })
  const lmsPassbackErrorMessage = ref<string | null>(null)

  // Getters
  const maxScore = computed<number>(() => gradingSettings.value?.maxScore ?? 0)
  const wallGradeCalculationMethod = computed<WallGradeCalculationMethods>(
    () => gradingSettings.value?.gradeCalculationMethod as WallGradeCalculationMethods,
  )
  const lmsPassbackEnabled = computed<boolean>(() => gradingSettings.value?.lmsPassbackEnabled ?? false)
  const showLmsPassbackToggle = computed<boolean>(() => gradingSettings.value?.showLmsPassback ?? false)
  const userSyncStateMap = computed<any>(() => {
    const syncMap = {}

    if (syncStateToUserId.value === null) {
      return syncMap
    }

    syncStateToUserId.value.failedUserIds?.forEach((userId) => {
      syncMap[userId] = UserSyncState.Failed
    })

    syncStateToUserId.value.successfulUserIds?.forEach((userId) => {
      syncMap[userId] = UserSyncState.Synced
    })
    syncStateToUserId.value.skippedUserIds?.forEach((userId) => {
      syncMap[userId] = UserSyncState.Skipped
    })

    return syncMap
  })

  const rubric = computed<string>(() => gradingSettings.value?.rubric ?? '')

  function getPostGrade(postId: number): Grade | null {
    const postGrade = grades.value.find((grade) => grade.gradableType === Gradable.Wish && grade.gradableId === postId)
    return postGrade ?? null
  }

  function getWallGrade(authorId: Id): Grade | null {
    const wallGrade = grades.value.find(
      (grade) => grade.gradableType === Gradable.Wall && grade.gradedUserId === authorId,
    )
    return wallGrade ?? null
  }

  function getCalculatedWallGrade(authorId: Id): number | null {
    const calculatedWallGrade = calculatedWallGrades.value.find((grade) => grade.authorId === authorId)
    return calculatedWallGrade?.calculatedGrade ?? null
  }

  function updateLmsSyncStateOnGradeChange(): void {
    if (gradingSettings.value?.lmsSyncState !== LmsSyncState.AwaitingFirstSync) {
      gradingSettings.value = {
        ...gradingSettings.value,
        lmsSyncState: LmsSyncState.NeedsSync,
      }
    }
  }

  async function createPostGrade(payload: {
    wishId: string
    wishAuthorId: string
    score: number
  }): Promise<Grade | null> {
    try {
      const postGrade = {
        gradableType: Gradable.Wish,
        gradableId: Number(payload.wishId),
        gradedUserId: Number(payload.wishAuthorId),
        category: GradeCategory.Score,
        value: payload.score.toString(),
      }
      const grade = await PadletApi.Grades.create(postGrade)
      trackEvent('Surface Grading Panel', 'Add Wish Grade', surfaceStore.wallId, {
        wall_id: surfaceStore.wallId,
        wish_id: payload.wishId,
        value: payload.score.toString(),
      })
      grades.value.push(postGrade)

      updateLmsSyncStateOnGradeChange()
      return grade
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating grade'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      return null
    }
  }

  async function createWallGrade(payload: {
    wallId: string
    gradedUserId: string
    comment: string
  }): Promise<Grade | null> {
    try {
      const wallGrade = {
        gradableType: Gradable.Wall,
        gradableId: Number(payload.wallId),
        gradedUserId: Number(payload.gradedUserId),
        category: GradeCategory.Comment,
        value: payload.comment,
      }
      const grade = await PadletApi.Grades.create(wallGrade)
      trackEvent('Surface Grading Panel', 'Add Wall Grade', surfaceStore.wallId, {
        wall_id: surfaceStore.wallId,
        comment: payload.comment,
      })
      grades.value.push(wallGrade)
      updateLmsSyncStateOnGradeChange()
      return grade
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating grade'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      return null
    }
  }

  async function createSuggestedComment(gradedUserId: string): Promise<string | null> {
    try {
      const suggestedCommentGradePayload = {
        wallId: surfaceStore.wallId,
        gradedUserId: Number(gradedUserId),
      }
      const comment = await PadletApi.AiSuggestedComments.create(suggestedCommentGradePayload)
      return comment
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error suggesting overall feedback with AI. Please try again later.'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      return null
    }
  }

  async function createSuggestedRubrics(): Promise<string | null> {
    try {
      const suggestedRubricsPayload = {
        wallId: surfaceStore.wallId,
      }
      const rubric = await PadletApi.AiSuggestedRubrics.create(suggestedRubricsPayload)
      return rubric
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error suggesting rubric with AI. Please try again later.'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      return null
    }
  }

  async function updateGrade(grade: Grade): Promise<Grade | null> {
    try {
      grades.value = grades.value.filter(
        (currentGrade) =>
          currentGrade.gradableType !== grade.gradableType ||
          currentGrade.gradableId !== grade.gradableId ||
          currentGrade.gradedUserId !== grade.gradedUserId,
      )
      grades.value.push(grade)
      updateLmsSyncStateOnGradeChange()
      return await PadletApi.Grades.update(grade)
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error updating grade'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      return null
    }
  }

  async function deleteGrade(grade: Grade): Promise<void> {
    try {
      const gradeApiResponse = await PadletApi.Grades.delete(grade)

      grades.value = grades.value.filter(
        (currentGrade) =>
          currentGrade.gradableType !== grade.gradableType ||
          currentGrade.gradableId !== grade.gradableId ||
          currentGrade.gradedUserId !== grade.gradedUserId,
      )

      updateLmsSyncStateOnGradeChange()
      return gradeApiResponse
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error deleting grade'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
    }
  }
  const lmsSyncState = computed<LmsSyncState>(() => {
    return gradingSettings.value?.lmsSyncState ?? LmsSyncState.AwaitingFirstSync
  })

  const syncMessage = computed<string>(() => {
    switch (lmsSyncState.value) {
      case LmsSyncState.Loading:
        return __('Syncing with LMS')
      case LmsSyncState.AwaitingFirstSync:
        return __('Not Synced')
      case LmsSyncState.NeedsSync:
      case LmsSyncState.Synced: {
        return __('Last synced: %{lastSynced}', { lastSynced: gradingSettings.value?.lmsLastSynced ?? 'Never' })
      }
      case LmsSyncState.Failed:
        return lmsPassbackErrorMessage.value ?? __('[Failed to sync with LMS]')
    }

    return __('Syncing with LMS')
  })

  // Actions
  async function initialize(): Promise<void> {
    isAwaitingGradingPanelApiResponse.value = true

    try {
      const wallId = surfaceStore.wallId
      if (useSurfacePermissionsStore().canIAdminister) {
        const [wallGradingSettingsResponse, gradesResponse, userSyncStateResponse] = await Promise.all([
          PadletApi.WallGradingSettings.fetch(wallId),
          PadletApi.Grades.fetch(wallId),
          PadletApi.LmsPassback.fetchUserSyncState(wallId),
        ])
        grades.value = gradesResponse
        gradingSettings.value = wallGradingSettingsResponse as WallGradingSettings
        syncStateToUserId.value = userSyncStateResponse as SyncStateUserIds
      } else if (surfaceStore.user.id != null) {
        const [wallGradingSettingsResponse, gradesResponse] = await Promise.all([
          PadletApi.WallGradingSettings.fetch(wallId),
          PadletApi.Grades.fetch(wallId, surfaceStore.user.id),
        ])
        gradingSettings.value = wallGradingSettingsResponse as WallGradingSettings
        grades.value = gradesResponse
      }
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceGradingPanelFetch' })
    }

    isAwaitingGradingPanelApiResponse.value = false
  }

  function showSurfaceGradingPanel(): void {
    trackEvent('Surface Grading Panel', 'Open Surface Grading Panel', surfaceStore.wallId, {
      wall_id: surfaceStore.wallId,
    })
    xSurfaceGradingPanel.value = true
    setSearchParam('grading_panel_open', 'true')
  }

  function hideSurfaceGradingPanel(): void {
    xSurfaceGradingPanel.value = false
    clearSearchParam('grading_panel_open')
  }

  async function updateMaxScore(updatedMaxScore: number): Promise<void> {
    const response = await PadletApi.WallGradingSettings.update(surfaceStore.wallId, {
      maxScore: updatedMaxScore,
    })
    gradingSettings.value = response as WallGradingSettings
  }

  async function toggleLmsPassbackEnabled(): Promise<void> {
    trackEvent('Surface Grading Panel', 'Enable Lms Passback', surfaceStore.wallId, {
      wall_id: surfaceStore.wallId,
    })
    const response = await PadletApi.WallGradingSettings.update(surfaceStore.wallId, {
      lmsPassbackEnabled: !lmsPassbackEnabled.value,
    })
    gradingSettings.value = {
      ...{ showLmsPassback: showLmsPassbackToggle.value },
      ...(response as WallGradingSettings),
    }
  }

  async function updateGradeCalculationMethod(
    updatedGradeCalculationMethod: WallGradeCalculationMethods,
  ): Promise<void> {
    const response = await PadletApi.WallGradingSettings.update(surfaceStore.wallId, {
      gradeCalculationMethod: updatedGradeCalculationMethod,
    })
    gradingSettings.value = response as WallGradingSettings
  }

  async function updateRubric(updatedRubric: string): Promise<void> {
    try {
      const response = await PadletApi.WallGradingSettings.update(surfaceStore.wallId, {
        rubric: updatedRubric,
      })
      gradingSettings.value = response as WallGradingSettings
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'Unable to update rubric' })
    }
  }

  async function syncGradeWithLms(): Promise<void> {
    try {
      lmsPassbackErrorMessage.value = null
      gradingSettings.value = { ...gradingSettings.value, lmsSyncState: LmsSyncState.Loading }
      syncStateToUserId.value = null

      const response = await PadletApi.LmsPassback.syncWithLms(surfaceStore.wallId)
      const data = (response.data as JsonApiData<LmsPassbackResponse>)?.attributes

      gradingSettings.value = { ...gradingSettings.value, ...data }
      if (data.lmsSyncState === LmsSyncState.Failed) {
        lmsPassbackErrorMessage.value = data.lmsSyncMessage ?? null
      }
      syncStateToUserId.value = data.userSyncState
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceGradingPanelFetch' })
      lmsPassbackErrorMessage.value = e.message
      gradingSettings.value = { ...gradingSettings.value, lmsSyncState: LmsSyncState.Failed }
    }
  }

  return {
    // State
    xSurfaceGradingPanel,
    isAwaitingGradingPanelApiResponse,
    grades,
    calculatedWallGrades,
    UserSyncState,

    // Getters
    maxScore,
    wallGradeCalculationMethod,
    lmsPassbackEnabled,
    rubric,
    showLmsPassbackToggle,
    syncMessage,
    lmsSyncState,
    userSyncStateMap,

    // Actions
    initialize,
    updateMaxScore,
    toggleLmsPassbackEnabled,
    updateGradeCalculationMethod,
    updateRubric,
    getPostGrade,
    getWallGrade,
    getCalculatedWallGrade,
    createPostGrade,
    createWallGrade,
    createSuggestedComment,
    createSuggestedRubrics,
    updateGrade,
    deleteGrade,
    showSurfaceGradingPanel,
    hideSurfaceGradingPanel,
    syncGradeWithLms,
  }
})
